/*
 * SignaturesSummaryPie — mostra o percentual global de assinaturas do painel
 * de Cobrança em formato de rosquinha (donut SVG puro, sem dependências),
 * com breakdown por categoria ao lado. Aceita filtro por PERÍODO (as_of):
 * botões rápidos (Hoje, 7d, 15d, 30d, 60d) e input de data custom.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Loader2, CheckCircle2, AlertCircle, PieChart as PieIcon,
  CalendarClock, RotateCcw } from "lucide-react";

const CATEGORY_ORDER = ["recibos", "avisos", "kit", "rubricas", "fotos"];
const CATEGORY_COLORS = {
  recibos:  "#f59e0b",  // amber
  avisos:   "#f97316",  // orange
  kit:      "#8b5cf6",  // violet
  rubricas: "#0ea5e9",  // sky
  fotos:    "#10b981",  // emerald
};

const PRESETS = [
  { key: "today", label: "Hoje",    days: 0 },
  { key: "7d",    label: "7 dias",  days: 7 },
  { key: "15d",   label: "15 dias", days: 15 },
  { key: "30d",   label: "30 dias", days: 30 },
  { key: "60d",   label: "60 dias", days: 60 },
];

function daysAgoIsoDate(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmtDateBr(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

function polar(cx, cy, r, angleRad) {
  return [cx + r * Math.cos(angleRad), cy + r * Math.sin(angleRad)];
}

function arcPath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  const [x0, y0] = polar(cx, cy, rOuter, startAngle);
  const [x1, y1] = polar(cx, cy, rOuter, endAngle);
  const [x2, y2] = polar(cx, cy, rInner, endAngle);
  const [x3, y3] = polar(cx, cy, rInner, startAngle);
  return [
    `M ${x0} ${y0}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x3} ${y3}`,
    "Z",
  ].join(" ");
}

function CategoryDonut({ categories, globalPercent, globalSigned, globalTotal }) {
  const size = 180;
  const cx = size / 2, cy = size / 2;
  const rOuter = 80, rInner = 52;

  const activeCats = CATEGORY_ORDER
    .map((k) => ({ key: k, ...(categories[k] || {}) }))
    .filter((c) => c.signed > 0);

  const totalSigned = activeCats.reduce((acc, c) => acc + c.signed, 0);
  let angle = -Math.PI / 2;
  const signedShare = globalTotal > 0 ? globalSigned / globalTotal : 0;

  const wedges = activeCats.map((c) => {
    const share = c.signed / totalSigned;
    const start = angle;
    const end = angle + share * Math.PI * 2 * signedShare;
    angle = end;
    return {
      key: c.key,
      color: CATEGORY_COLORS[c.key],
      path: arcPath(cx, cy, rOuter, rInner, start, end),
      label: c.label,
      signed: c.signed,
      total: c.total,
    };
  });
  const pendingPath = arcPath(cx, cy, rOuter, rInner, angle, Math.PI * 1.5);

  return (
    <div className="flex items-center justify-center relative">
      <svg width={size} height={size}
            viewBox={`0 0 ${size} ${size}`}
            data-testid="signatures-pie-svg">
        <circle cx={cx} cy={cy} r={rOuter} fill="#f1f5f9" />
        <circle cx={cx} cy={cy} r={rInner} fill="#ffffff" />
        {globalTotal > 0 && globalSigned < globalTotal && (
          <path d={pendingPath} fill="#e2e8f0" />
        )}
        {wedges.map((w) => (
          <path key={w.key} d={w.path} fill={w.color}>
            <title>{w.label}: {w.signed}/{w.total}</title>
          </path>
        ))}
        <text x={cx} y={cy - 6}
              textAnchor="middle" dominantBaseline="middle"
              fontFamily="ui-sans-serif, system-ui"
              fontSize="26" fontWeight="700" fill="#0f172a">
          {globalPercent.toFixed(1)}%
        </text>
        <text x={cx} y={cy + 18}
              textAnchor="middle" dominantBaseline="middle"
              fontFamily="ui-sans-serif, system-ui"
              fontSize="11" fill="#64748b">
          assinado
        </text>
      </svg>
    </div>
  );
}

export default function SignaturesSummaryPie() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState("today");
  const [asOf, setAsOf] = useState("");

  const fetchSummary = useCallback(async (asOfParam) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/cobranca/summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: asOfParam ? { as_of: asOfParam } : {},
        timeout: 20000,
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao carregar.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSummary(""); }, [fetchSummary]);

  const applyPreset = (p) => {
    setPreset(p.key);
    if (p.days === 0) {
      setAsOf(""); fetchSummary("");
    } else {
      const d = daysAgoIsoDate(p.days);
      setAsOf(d);
      fetchSummary(d);
    }
  };

  const applyCustom = (dateStr) => {
    setPreset("custom");
    setAsOf(dateStr);
    fetchSummary(dateStr || "");
  };

  const g = data?.global || {};
  const cats = data?.categories || {};
  const isHistorical = !!asOf;

  return (
    <Card className={`mb-4 ${isHistorical ? "border-indigo-300 ring-1 ring-indigo-100" : "border-slate-200"}`}
           data-testid="signatures-summary-card">
      <CardContent className="p-4 sm:p-5">
        {/* Period selector */}
        <div className="flex items-center gap-2 flex-wrap mb-3 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-1.5 text-slate-600 mr-1">
            <CalendarClock className="w-4 h-4" />
            <span className="text-xs font-semibold">Período:</span>
          </div>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              data-testid={`signatures-preset-${p.key}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${
                preset === p.key
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-1">
            <Input
              type="date"
              value={asOf}
              onChange={(e) => applyCustom(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="h-7 text-[11px] w-[140px]"
              data-testid="signatures-custom-date"
            />
            {isHistorical && (
              <button type="button"
                       onClick={() => applyPreset(PRESETS[0])}
                       title="Voltar para hoje"
                       data-testid="signatures-reset"
                       className="p-1 rounded hover:bg-slate-100">
                <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
              </button>
            )}
          </div>
          {isHistorical && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 font-mono ml-auto"
                   data-testid="signatures-snapshot-label">
              Snapshot em {fmtDateBr(asOf)}
            </span>
          )}
        </div>

        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1 mb-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}

        {loading && !data ? (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
            Carregando…
          </div>
        ) : data && (
          <div className={`flex flex-col md:flex-row items-center gap-6 ${loading ? "opacity-60" : ""}`}>
            <div className="shrink-0">
              <CategoryDonut
                categories={cats}
                globalPercent={g.percent || 0}
                globalSigned={g.signed || 0}
                globalTotal={g.total || 0}
              />
            </div>

            <div className="flex-1 min-w-0 w-full">
              <div className="flex items-center gap-2 mb-2">
                <PieIcon className="w-4 h-4 text-emerald-700" />
                <p className="text-sm font-semibold text-slate-900">
                  Assinaturas do painel
                </p>
                <span className="text-[11px] text-slate-500 font-mono">
                  {g.signed} / {g.total} · {g.pending} pendente(s)
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                    data-testid="signatures-summary-legend">
                {CATEGORY_ORDER.map((k) => {
                  const c = cats[k] || {};
                  const done = c.total > 0 && c.pending === 0;
                  return (
                    <div key={k}
                          className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200"
                          data-testid={`signatures-summary-${k}`}>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0"
                             style={{ backgroundColor: CATEGORY_COLORS[k] }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-800 truncate">
                          {c.label || k}
                        </p>
                        <p className="text-[10px] text-slate-500 font-mono">
                          {c.signed || 0}/{c.total || 0} ·{" "}
                          <span className={
                            done ? "text-emerald-700 font-semibold" : ""
                          }>
                            {(c.percent || 0).toFixed(1)}%
                          </span>
                        </p>
                      </div>
                      {done && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
